import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";

export default function Shop() {
  const { t } = useI18n();
  const { addProduct } = useCart();
  const { user } = useAuth();
  const [products, setProducts] = useState(null);

  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([])); }, []);

  const add = (p) => {
    if (!user) { window.location.href = "/login"; return; }
    if (p.stock <= 0) return;
    addProduct(p, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#5C061C]">{t("shop.title")}</h1>
        <p className="mt-2 text-[#6E6259] max-w-2xl">{t("shop.subtitle")}</p>
      </div>

      {!products ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <Skeleton key={i} className="h-96 rounded-2xl" />)}
        </div>
      ) : products.length === 0 ? (
        <p className="text-[#6E6259]">No products available.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <div key={p.id} className="card-warm rounded-2xl overflow-hidden" data-testid={`product-card-${p.id}`}>
              <div className="aspect-square bg-[#F4EEDF]">
                {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif text-xl text-[#5C061C]">{p.name}</h3>
                  <Badge className={p.stock > 0 ? "bg-[#15803D]/10 text-[#15803D]" : "bg-[#DC2626]/10 text-[#DC2626]"}>
                    {p.stock > 0 ? t("shop.in_stock") : t("shop.out_of_stock")}
                  </Badge>
                </div>
                <p className="text-sm text-[#6E6259] mt-1">{p.description}</p>
                <p className="text-xs text-[#B45309] mt-2 uppercase tracking-widest">{t("shop.pickup_only")}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-2xl font-serif text-[#5C061C]">₹{p.price}</span>
                  <Button onClick={() => add(p)} disabled={p.stock <= 0} className="btn-saffron rounded-full h-11 px-5" data-testid={`add-to-cart-${p.id}`}>
                    <ShoppingBag className="h-4 w-4 mr-2" /> {t("shop.add_to_cart")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
